import qs from 'query-string';
import React from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@99designs/common/components/Icon';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    path: string;
    searchString?: string;
    onClick?: () => void;
}

class Pagination extends React.Component<PaginationProps> {
    static readonly defaultProps = {
        searchString: '',
        onClick: () => {},
    };

    prevLinkHtml(i: number, path: string, disabled: boolean) {
        let prevLink: React.ReactNode = '';
        if (disabled) {
            prevLink = (
                <span className="pagination__item pagination__prev" key="paginationButtonPrev">
                    <span className="pagination__button" data-disabled="true">
                        <span className="pagination__button__inner">
                            <span className="pagination__button__icon">
                                <span className="icon icon--left-2" />
                            </span>
                        </span>
                    </span>
                </span>
            );
        } else {
            prevLink = (
                <span className="pagination__item pagination__prev" key="paginationButtonPrev">
                    <Link
                        to={this.buildPath(i)}
                        onClick={this.props.onClick}
                        className="pagination__button"
                    >
                        <span className="pagination__button__inner">
                            <span className="pagination__button__icon">
                                <Icon type={'left-2'} />
                            </span>
                        </span>
                    </Link>
                </span>
            );
        }
        return prevLink;
    }

    nextLinkHtml(i: number, path: string, disabled: boolean) {
        let nextLink: React.ReactNode = '';
        if (disabled) {
            nextLink = (
                <span className="pagination__item pagination__next" key="paginationButtonNext">
                    <span className="pagination__button" data-disabled="true">
                        <span className="pagination__button__inner">
                            <span className="pagination__button__icon">
                                <span className="icon icon--right-2" />
                            </span>
                        </span>
                    </span>
                </span>
            );
        } else {
            nextLink = (
                <span className="pagination__item pagination__next" key="paginationButtonNext">
                    <Link
                        to={this.buildPath(i)}
                        onClick={this.props.onClick}
                        className="pagination__button"
                    >
                        <span className="pagination__button__inner">
                            <span className="pagination__button__icon">
                                <Icon type={'right-2'} />
                            </span>
                        </span>
                    </Link>
                </span>
            );
        }
        return nextLink;
    }

    buildPath(pageNumber: number) {
        const existingSearch = qs.parse(this.props.searchString || '', { arrayFormat: 'bracket' });
        const query = { ...existingSearch, page: pageNumber };
        return `${this.props.path}?${qs.stringify(query, { arrayFormat: 'bracket' })}`;
    }

    pagePlaceholder() {
        const placeholderClass =
            'pagination__item pagination__placeholder pagination--hide-small-screens';
        return (
            <span className={placeholderClass} key="numberPlaceholder">
                …
            </span>
        );
    }

    render() {
        const paginationButtons = [];
        const currentPage = this.props.currentPage;
        const totalPages = this.props.totalPages;
        const path = this.props.path;

        paginationButtons.push(this.prevLinkHtml(currentPage - 1, path, currentPage === 1));

        paginationButtons.push(
            <span className="pagination__item pagination__summary" key="paginationSummary">
                {currentPage} of {totalPages}
            </span>
        );

        let startingPage = currentPage - 1;
        if (startingPage <= 0) {
            startingPage = 1;
        }
        if (startingPage > 1) {
            paginationButtons.push(
                <span className="pagination__item pagination__number" key="paginationButtonFirst">
                    <Link
                        to={this.buildPath(1)}
                        onClick={this.props.onClick}
                        className="pagination__title pagination--hide-small-screens"
                    >
                        1
                    </Link>
                </span>
            );
            if (totalPages > 3) {
                paginationButtons.push(this.pagePlaceholder());
            }
        }

        let endingPage = currentPage + 1;
        if (endingPage >= totalPages) {
            endingPage = totalPages;
        }

        for (let i = startingPage; i <= endingPage; i += 1) {
            let className = 'pagination__title';
            const active: {
                'data-active'?: boolean;
            } = {};
            if (i !== currentPage) {
                className += ' pagination--hide-small-screens';
            } else {
                active['data-active'] = true;
            }
            paginationButtons.push(
                <span
                    className="pagination__item pagination__number"
                    key={`paginationButton${i}`}
                    {...active}
                >
                    <Link to={this.buildPath(i)} onClick={this.props.onClick} className={className}>
                        {i}
                    </Link>
                </span>
            );
        }

        if (endingPage < totalPages) {
            if (totalPages > 3) {
                paginationButtons.push(this.pagePlaceholder());
            }
            paginationButtons.push(
                <span className="pagination__item pagination__number" key="paginationButtonLast">
                    <Link
                        to={this.buildPath(totalPages)}
                        onClick={this.props.onClick}
                        className="pagination__title pagination--hide-small-screens"
                    >
                        {totalPages}
                    </Link>
                </span>
            );
        }

        paginationButtons.push(
            this.nextLinkHtml(currentPage + 1, path, currentPage === totalPages)
        );

        let pagination;
        if (totalPages > 1) {
            pagination = <section className="pagination">{paginationButtons}</section>;
        } else {
            pagination = <span />;
        }

        return pagination;
    }
}

export default Pagination;



// WEBPACK FOOTER //
// ./src/js/components/pagination.tsx